/**
 * StructuredData Component
 * 
 * Project: SunMarbella Holiday Rental Website
 * Version: 1.0.0
 * Last Modified: 2025-03-09
 * 
 * Purpose:
 * Generates and injects JSON-LD structured data for SEO. Provides rich snippets for search engines.
 * 
 * Features:
 * - Organization schema
 * - Property/LocalBusiness schema
 * - BreadcrumbList schema
 * - FAQPage schema
 * - Automatic injection via script tags
 * 
 * Dependencies:
 * - React 19.2.4 (useEffect)
 * 
 * Changelog:
 * - 1.0.0 (2025-03-09): Initial implementation
 */

import React from 'react';
import { COMPANY_INFO } from '../config/company';

interface Property {
  id: string;
  title: string;
  location: string;
  price: number;
  bedrooms: number;
  bathrooms: number;
  guests: number;
  images: string[];
  description?: string;
  rating?: number;
  reviews?: number;
}

// Lodging Business Schema
export const lodgingBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "LodgingBusiness",
  "@id": COMPANY_INFO.websiteUrl,
  "name": COMPANY_INFO.brandName,
  "description": "Inspected holiday rentals in Marbella with direct booking and local support",
  "url": COMPANY_INFO.websiteUrl,
  "telephone": COMPANY_INFO.phone,
  "email": COMPANY_INFO.email,
  "address": {
    "@type": "PostalAddress",
    "addressCountry": COMPANY_INFO.address.countryCode,
    "addressRegion": "AN",
    "addressLocality": COMPANY_INFO.address.locality,
    "postalCode": COMPANY_INFO.address.postalCode,
    "streetAddress": COMPANY_INFO.address.streetAddress
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 36.5103,
    "longitude": -4.8875
  },
  "sameAs": [
    "https://instagram.com/sunmarbella",
    "https://facebook.com/sunmarbella",
    "https://x.com/sunmarbella"
  ],
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.9",
    "reviewCount": "527",
    "bestRating": "5",
    "worstRating": "1"
  },
  "priceRange": "$$$"
};

// Vacation Rental Schema for individual properties
export const createVacationRentalSchema = (property: Property) => ({
  "@context": "https://schema.org",
  "@type": "VacationRental",
  "@id": `https://sunmarbella.com/property/${property.id}`,
  "name": property.title,
  "description": property.description || `Luxury ${property.bedrooms}-bedroom holiday rental in ${property.location}`,
  "url": `https://sunmarbella.com/property/${property.id}`,
  "image": property.images,
  "address": {
    "@type": "PostalAddress",
    "addressCountry": "ES",
    "addressRegion": "AN",
    "addressLocality": property.location
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 36.5103,
    "longitude": -4.8875
  },
  "numberOfBedrooms": property.bedrooms,
  "numberOfBathrooms": property.bathrooms,
  "occupancy": {
    "@type": "QuantitativeValue",
    "maxValue": property.guests
  },
  "amenityFeature": [
    {
      "@type": "LocationFeatureSpecification",
      "name": "Pool",
      "value": true
    },
    {
      "@type": "LocationFeatureSpecification", 
      "name": "WiFi",
      "value": true
    },
    {
      "@type": "LocationFeatureSpecification",
      "name": "Air Conditioning",
      "value": true
    }
  ],
  "offers": {
    "@type": "Offer",
    "price": property.price,
    "priceCurrency": "EUR",
    "availability": "https://schema.org/InStock",
    "validFrom": new Date().toISOString()
  },
  "aggregateRating": property.rating ? {
    "@type": "AggregateRating",
    "ratingValue": property.rating.toString(),
    "reviewCount": property.reviews?.toString() || "0"
  } : undefined,
  "speakable": {
    "@type": "SpeakableSpecification",
    "cssSelector": ["h1", "description"]
  }
});

// FAQ Schema
export const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Do you provide airport transfers in Marbella?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. We can arrange private Malaga Airport transfers directly to your rental."
      }
    },
    {
      "@type": "Question",
      "name": "What is the minimum stay?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Minimum stays vary by property and season. Most properties require 7 nights in peak periods and shorter stays in quieter months."
      }
    },
    {
      "@type": "Question",
      "name": "Are your rentals family-friendly?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. Many rentals include family-friendly layouts, and we can provide cots, high chairs, and child-focused recommendations."
      }
    },
    {
      "@type": "Question",
      "name": "What is included in the rental price?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Rates usually include accommodation, utilities, WiFi, and standard check-in support. Final cleaning and extras depend on the property."
      }
    }
  ]
};

// BreadcrumbList Schema
export const createBreadcrumbSchema = (breadcrumbs: Array<{ name: string; url: string }>) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": breadcrumbs.map((crumb, idx) => ({
    "@type": "ListItem",
    "position": idx + 1,
    "name": crumb.name,
    "item": crumb.url
  }))
});

// Website Schema for LLM optimization
export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": COMPANY_INFO.websiteUrl,
  "url": COMPANY_INFO.websiteUrl,
  "name": COMPANY_INFO.brandName,
  "description": "Inspected holiday rentals in Marbella, Spain with direct booking and local support.",
  "publisher": {
    "@type": "Organization",
    "name": COMPANY_INFO.brandName,
    "url": COMPANY_INFO.websiteUrl
  },
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": `${COMPANY_INFO.websiteUrl}/destinations?search={search_term_string}`
    },
    "query-input": "required name=search_term_string"
  }
};
